import { inject, observer } from 'mobx-react';
import { Link as RouterLink } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';
import { QueryRoute, RouteStore } from 'bernie-core';

import { HotelMapModel } from 'models/hotel/map-types';
import { OverviewViewModel } from 'models';

import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';

// Components
import { QueuedImage } from 'components/common/media/queued-image';
import { MapAddressLine } from 'components/infosite/property-details/property-map/map-address-line';
import { MapDialog } from 'components/infosite/property-details/property-map/map-dialog';

const LinkTracked = ClickTracker(UitkLink);

interface MapTriggerState {
  mapActive: boolean;
}

interface MapTriggerProps {
  alt: string;
  className?: string;
  label?: string;
  mapModel: HotelMapModel;
  overview?: OverviewViewModel;
  routeStore?: RouteStore;
  src?: string;
}

@inject('routeStore')
@observer
export class MapTrigger extends React.Component<MapTriggerProps, MapTriggerState> {
  private mapBtn: any;

  constructor(props) {
    super(props);

    this.state = {
      mapActive: false
    };

    this.onMapDialogClose = this.onMapDialogClose.bind(this);
  }

  public shouldComponentUpdate(nextState) {
    return this.state.mapActive !== nextState.mapActive;
  }

  public componentDidMount() {
    this.setState({
      mapActive: true
    });
  }

  public render() {
    const { routeStore, overview, src, className, mapModel } = this.props;
    const { mapActive } = this.state;
    const mapBtn = m => {
      this.mapBtn = m;
    };
    const searchName = src ? 'map' : 'address';
    const mapOpenQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialog: searchName });
    const extraProps = mapActive ? { innerRef: mapBtn } : {};
    const classList = src ? ['map'] : ['address', 'uitk-type-left'];

    if (className) {
      classList.push(className);
    }

    const mapLinkProps = {
      overview,
      routeStore,
      mapOpenQuery,
      mapActive
    };

    if (mapActive) {
      return (
        <React.Fragment>
          <LinkTracked
            className={classList.join(' ')}
            disabled={!mapActive || null}
            inline={true}
            linkName="Click static map"
            rfrr={src ? 'Map.StaticMap' : 'ClickAddressViewMap'}
            {...extraProps}
          >
            <RouterLink to={{ pathname: routeStore.pathname, search: mapOpenQuery }}>
              {src && (
                <QueuedImage
                  className="map__image image-loader image-loader__no-scrim"
                  src={src}
                  useBackgroundImage={true}
                  serverRender={true}
                />
              )}
              {!src && (
                <AbacusExperiment name="PWA_Whats_Around_Map_CTA">
                  <AbacusControl>
                    <MapAddressLine overview={overview} />
                  </AbacusControl>
                  <AbacusVariant bucket={1} />
                  <AbacusVariant bucket={2} />
                </AbacusExperiment>
              )}
              <span className="is-visually-hidden">{`${mapModel.title} Map`}</span>
            </RouterLink>
          </LinkTracked>
          {!src && (
            <AbacusExperiment name="PWA_Whats_Around_Map_CTA">
              <AbacusControl />
              <AbacusVariant bucket={1}>
                <MapCtaLink {...mapLinkProps} />
              </AbacusVariant>
              <AbacusVariant bucket={2}>
                <MapAddressWithCtaLink {...mapLinkProps} />
              </AbacusVariant>
            </AbacusExperiment>
          )}
          <QueryRoute query={{ pwaDialog: searchName }}>
            <Layer id="infosite-map" onDismiss={this.onMapDialogClose}>
              <MapDialog mapModel={mapModel} onDismiss={this.onMapDialogClose} />
            </Layer>
          </QueryRoute>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          {src && (
            <QueuedImage
              className="map__image image-loader image-loader__no-scrim is-disabled"
              src={src}
              useBackgroundImage={true}
              serverRender={true}
            />
          )}
          {!src && <MapAddressLine className="uitk-link is-disabled" overview={overview} />}
        </React.Fragment>
      );
    }
  }

  private onMapDialogClose() {
    this.props.routeStore.goBack();

    if (this.mapBtn && typeof this.mapBtn.focus === 'function') {
      this.mapBtn.focus();
    }
  }
}

const MapCtaLink = ({ overview, routeStore, mapOpenQuery, mapActive }) => (
  <div className="uitk-grid all-cell-1-2" data-stid="map-cta-link">
    <MapAddressLine className="pwa-theme--grey-700 uitk-cell all-cell-fill" overview={overview} />
    <LinkTracked className="uitk-type-left" disabled={!mapActive} inline={true} linkName="ClickLinkViewMap" rfrr="ClickLinkViewMap">
      <RouterLink to={{ pathname: routeStore.pathname, search: mapOpenQuery }}>
        <ShowMapText />
      </RouterLink>
    </LinkTracked>
  </div>
);

const MapAddressWithCtaLink = ({ overview, routeStore, mapOpenQuery, mapActive }) => (
  <LinkTracked
    className="uitk-type-left"
    disabled={!mapActive}
    inline={true}
    linkName="ClickAddressCTALinkViewMap"
    rfrr="ClickAddressCTALinkViewMap"
  >
    <RouterLink to={{ pathname: routeStore.pathname, search: mapOpenQuery }}>
      <div className="uitk-grid all-cell-1-2" data-stid="map-address-cta-link">
        <MapAddressLine className="uitk-cell all-cell-fill" overview={overview} />
        <ShowMapText />
      </div>
    </RouterLink>
  </LinkTracked>
);

const ShowMapText = () => (
  <span className=" all-l-padding-six uitk-cell all-cell-shrink">
    <LocalizedText message="hotels.searchResults.openMapDialog.label" />
    <UitkIcon name="chevron_right" />
  </span>
);
